import {yupResolver} from '@hookform/resolvers/yup';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import LoadingButton from '@mui/lab/LoadingButton';
import {
  Box,
  Stack,
  TextField,
  IconButton, Skeleton,
} from '@mui/material';
import _ from 'lodash';
import React, {type FC, useState} from 'react';
import {Controller, useForm} from 'react-hook-form';

import {useMutateUpdateUser} from '../../api/index.js';
import useSnackBar from '../../hooks/use-snack-bar.js';
import {type UpdateMonitorFormValues} from '../Forms/formTypes.js';
import {updateMonitorFormSchema} from '../Forms/validations.js';
import PasswordField from '../PasswordField.js';


const defaultValues: UpdateMonitorFormValues = {
  name: '',
  email: '',
  password: '',
};

interface MonitorInformationFormProps {
  values?: UpdateMonitorFormValues;
  monitorId: string;
}

const MonitorInformationForm: FC<MonitorInformationFormProps> = ({values, monitorId}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const {
    control,
    handleSubmit,
    reset,
    formState: {errors, isDirty},
  } = useForm({
    defaultValues,
    values,
    resolver: yupResolver(updateMonitorFormSchema),
  });
  const updateMutation = useMutateUpdateUser();
  const snackBar = useSnackBar();

  return (
    <Box sx={{minWidth: '500px', width: '500px'}}>
      <Stack direction={'row'} alignItems={'center'} spacing={1}>
        <h2 style={{margin: 0}}>Monitor's information</h2>
        <IconButton onClick={() => reset(values)} disabled={!isDirty}>
          <RestartAltIcon/>
        </IconButton>
      </Stack>
      <Box sx={{mt: 2}}>
        {values ?
          <form
            onSubmit={handleSubmit(async (data) => {
              setIsSubmitting(true);
              try {
                await updateMutation.mutateAsync({
                  id: monitorId,
                  name: data.name,
                  email: data.email,
                  ...(data.password && {password: data.password}),
                });
                snackBar.success(`Monitor's information updated successfully!`);
              } catch (e) {
                snackBar.error(`Monitor's information update failed!`);
              }
              setIsSubmitting(false);
            })}
          >
            <Stack spacing={2}>
              <Controller
                name={'name'}
                control={control}
                render={({field}) => (
                  <TextField
                    {...field}
                    InputLabelProps={{required: true}}
                    error={!!errors?.name}
                    helperText={errors?.name?.message}
                    label={'Name'}
                  />
                )}
              />
              <Controller
                name={'email'}
                control={control}
                render={({field}) => (
                  <TextField
                    {...field}
                    InputLabelProps={{required: true}}
                    error={!!errors?.email}
                    helperText={errors?.email?.message}
                    label={'Email'}
                  />
                )}
              />
              <PasswordField
                name={'password'}
                control={control}
                errors={errors}
              />
              <LoadingButton
                type={'submit'}
                loading={isSubmitting}
                variant={'contained'}
                disabled={!_.isEmpty(errors) || !isDirty}
              >
                Save
              </LoadingButton>
            </Stack>
          </form>
          :
          <>
            <Skeleton width={500} height={70}/>
            <Skeleton width={500} height={70}/>
            <Skeleton width={500} height={70}/>
            <Skeleton variant="rectangular" width={500} height={40}/>
          </>}
      </Box>
    </Box>
  );
};

export default MonitorInformationForm;
